import React from 'react';
import { Brain, Dumbbell, Compass, Palette } from 'lucide-react';
import { fourPillarsData } from '../data/mock';

const iconMap = {
  brain: Brain,
  dumbbell: Dumbbell,
  compass: Compass,
  palette: Palette
};

export const FourPillars = () => {
  return (
    <section id="pillars" className="relative py-24 bg-gunmetal-900">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 noise-texture"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-platinum animate-chrome-pulse">THE </span>
            <span className="text-accent-red">FOUR PILLARS</span>
          </h2>
          <div className="w-24 h-1 bg-accent-red mx-auto mb-6"></div>
          <p className="text-xl text-steel-400 max-w-3xl mx-auto leading-relaxed">
            Our signature philosophy for navigating life's challenges with grace, 
            power, and purpose. Built on our foundation: Live More, Love More, Laugh More, Give More.
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {fourPillarsData.map((pillar, index) => {
            const IconComponent = iconMap[pillar.icon];
            return (
              <div 
                key={pillar.id}
                className="group bg-gradient-to-br from-gunmetal-800/50 to-gunmetal-900/50 backdrop-blur-sm rounded-lg p-8 border border-steel-700/30 hover:border-accent-red/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-accent-red/20"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Icon */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="p-4 bg-accent-red/10 rounded-lg group-hover:bg-accent-red/20 transition-colors duration-300">
                    <IconComponent className="text-accent-red" size={40} />
                  </div>
                  <span className="text-6xl font-black text-steel-800 group-hover:text-steel-700 transition-colors duration-300">
                    0{pillar.id}
                  </span>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-steel-100 mb-4 group-hover:text-accent-red transition-colors duration-300">
                  {pillar.pillar}
                </h3>
                <p className="text-steel-400 mb-6 leading-relaxed">
                  {pillar.description}
                </p>

                {/* Practices */}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-steel-300 uppercase tracking-wider mb-3">
                    Key Practices:
                  </p>
                  {pillar.practices.map((practice, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-accent-red rounded-full"></div>
                      <span className="text-steel-400 text-sm">{practice}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Philosophy Statement */}
        <div className="bg-gunmetal-950/80 backdrop-blur-sm rounded-lg p-8 md:p-12 border-l-4 border-accent-red">
          <blockquote className="text-2xl md:text-3xl font-bold text-steel-200 italic leading-relaxed">
            "The Four Pillars aren't just principles—they're a blueprint for transformation. 
            When you strengthen each pillar, you build an unshakeable foundation for the life 
            you were meant to live."
          </blockquote>
          <p className="text-steel-400 mt-4 text-lg">
            — Jose Francisco Rodriguez Jr., Founder
          </p>
        </div>
      </div>
    </section>
  );
};
